(() => {
  const scriptCache = new Map();
  let shopperListenersBound = false;
  let stampedGlobalConfig = null;
  let stampedInitPromise = null;
  let stampedPreloadScheduled = false;
  let stampedBadgeObserver = null;
  const STAMPED_STYLE_URL = 'https://cdn1.stamped.io/files/widget.min.css';
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
  const USER_INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel'];

  let userHasInteracted = false;
  const interactionCallbacks = [];

  function primaryJqueryReady() {
    const promise = window.__primaryJqueryReady;
    if (promise && typeof promise.then === 'function') {
      return promise.catch(() => null);
    }
    if (window.jQuery && typeof window.jQuery.fn === 'object') {
      return Promise.resolve(window.jQuery);
    }
    if (window.$ && typeof window.$.fn === 'object') {
      return Promise.resolve(window.$);
    }
    return Promise.resolve(null);
  }

  function markUserInteraction() {
    if (userHasInteracted) return;
    userHasInteracted = true;
    while (interactionCallbacks.length) {
      const callback = interactionCallbacks.shift();
      try {
        callback();
      } catch (error) {
        console.error('Lazy integrations: interaction callback error', error);
      }
    }
  }

  function runAfterUserInteraction(callback, fallbackDelay = 8000) {
    let called = false;
    const wrapped = () => {
      if (called) return;
      called = true;
      try {
        callback();
      } catch (error) {
        console.error('Lazy integrations: interaction callback error', error);
      }
    };

    if (userHasInteracted) {
      wrapped();
      return;
    }
    interactionCallbacks.push(wrapped);
    if (fallbackDelay) {
      window.setTimeout(wrapped, fallbackDelay);
    }
  }

  USER_INTERACTION_EVENTS.forEach((eventName) => {
    const options = { once: true };
    if (eventName !== 'keydown') {
      options.passive = true;
    }
    window.addEventListener(eventName, markUserInteraction, options);
  });
  window.addEventListener('scroll', markUserInteraction, { once: true, passive: true });

  function loadScriptOnce(src) {
    if (!src) return Promise.resolve();
    if (scriptCache.has(src)) {
      return scriptCache.get(src);
    }

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      const ready = existing.dataset.lazyLoaded === 'true'
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', reject, { once: true });
          });
      scriptCache.set(src, ready);
      return ready;
    }

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.lazyLoaded = 'true';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    scriptCache.set(src, promise);
    return promise;
  }

  function attachActivationHandlers(element, handler) {
    if (!element) return;
    element.addEventListener('click', (event) => {
      event.preventDefault();
      handler(event);
    });
    element.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handler(event);
    });
  }

  function observeVisibility(element, callback, rootMargin = '0px 0px 200px 0px') {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }

    let triggered = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !triggered) {
          triggered = true;
          observer.disconnect();
          callback();
        }
      });
    }, { rootMargin });

    observer.observe(element);
  }

  function setStatus(container, message, state) {
    const status = container.querySelector('[data-integration-status]');
    if (!status) return;

    if (message) {
      status.textContent = message;
      status.hidden = false;
    } else {
      status.textContent = '';
      status.hidden = true;
    }

    if (state) {
      status.dataset.state = state;
    } else {
      delete status.dataset.state;
    }
  }

  function extractStampedConfig(container) {
    if (!container) return stampedGlobalConfig;

    const config = {
      scriptSrc: container.dataset.src,
      apiKey: container.dataset.apiKey,
      storeId: container.dataset.storeId,
    };

    if (config.scriptSrc) {
      stampedGlobalConfig = stampedGlobalConfig || config;
      return config;
    }

    return stampedGlobalConfig;
  }

  function ensureStampedStyle() {
    let link = document.querySelector('link[data-stamped-style]');
    if (link) return link;
    link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = STAMPED_STYLE_URL;
    link.media = 'all';
    link.dataset.stampedStyle = 'true';
    document.head.appendChild(link);
    return link;
  }

  function ensureStampedWidgetElement(container, target) {
    if (!container || !target) return null;
    if (container.dataset.stampedWidgetInjected === 'true') {
      return target.querySelector('#stamped-main-widget');
    }

    const template = container.dataset.stampedWidgetTemplate;
    if (!template) return target.querySelector('#stamped-main-widget');

    target.insertAdjacentHTML('beforeend', template);
    container.dataset.stampedWidgetInjected = 'true';
    return target.querySelector('#stamped-main-widget');
  }

  function ensureStampedInit(config) {
    const finalConfig = config || stampedGlobalConfig;
    if (!finalConfig || !finalConfig.scriptSrc) return Promise.resolve();

    if (!stampedGlobalConfig && finalConfig.scriptSrc) {
      stampedGlobalConfig = finalConfig;
    }

    if (!stampedInitPromise) {
      stampedInitPromise = primaryJqueryReady()
        .then((jq) => {
          if (jq) {
            if (!window.jQuery) window.jQuery = jq;
            if (!window.$) window.$ = jq;
            window.jQueryStamped = window.jQueryStamped || jq;
          }
          ensureStampedStyle();
          if (!window.StampedGlobalOptions) window.StampedGlobalOptions = {};
          window.StampedGlobalOptions.is_force_jquery = false;
          window.StampedGlobalOptions.exclude_jquery = true;
          window.dispatchEvent(new CustomEvent('lazy:stamped:request'));
          return loadScriptOnce(finalConfig.scriptSrc);
        })
        .then(() => {
          if (window.StampedFn && typeof window.StampedFn.init === 'function') {
            window.StampedFn.init({ apiKey: finalConfig.apiKey, sId: finalConfig.storeId });
          } else {
            console.warn('Stamped widget loaded but window.StampedFn.init is unavailable.');
          }
        })
        .catch((error) => {
          stampedInitPromise = null;
          throw error;
        });
    }

    return stampedInitPromise;
  }

  function scheduleStampedPreload(scope) {
    if (stampedPreloadScheduled) return;

    const badge = scope.querySelector('.stamped-product-reviews-badge');
    if (!badge) {
      if (!stampedBadgeObserver && 'MutationObserver' in window) {
        stampedBadgeObserver = new MutationObserver((mutations) => {
          let found = false;
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (found || !(node instanceof Element)) return;
              if (node.matches && node.matches('.stamped-product-reviews-badge')) {
                found = true;
              } else if (node.querySelector && node.querySelector('.stamped-product-reviews-badge')) {
                found = true;
              }
            });
          });

          if (found) {
            scheduleStampedPreload(document);
          }
        });

        stampedBadgeObserver.observe(document.documentElement, { childList: true, subtree: true });
      }
      return;
    }

    if (stampedBadgeObserver) {
      stampedBadgeObserver.disconnect();
      stampedBadgeObserver = null;
    }

    const container = document.querySelector('[data-lazy-integration="stamped"]');
     if (!container) return;
    if (container.dataset.lazyAuto === 'true' && container.dataset.lazyPreload !== 'true') {
      return;
    }

    const config = extractStampedConfig(container);
    if (!config || !config.scriptSrc) return;

    stampedPreloadScheduled = true;
    stampedGlobalConfig = config;

    Promise.resolve().then(() => ensureStampedInit(config)).catch((error) => {
      console.error('Lazy integrations: unable to preload Stamped badge', error);
      stampedPreloadScheduled = false;
    });
  }

  function initStamped(container) {
    if (container.dataset.lazyReady === 'true') return;

    const trigger = container.querySelector('[data-integration-trigger]');
    const target = container.querySelector('[data-integration-target]');
    if (!target) return;
    const placeholder = target.querySelector('[data-integration-placeholder]');
    const originalStyle = target.dataset.originalStyle || target.getAttribute('style') || '';
    target.dataset.originalStyle = originalStyle;

    const scriptSrc = container.dataset.src;
    const apiKey = container.dataset.apiKey;
    const storeId = container.dataset.storeId;
    const auto = container.dataset.lazyAuto === 'true';
    const threshold = container.dataset.lazyThreshold || '200';
    const status = container.querySelector('[data-integration-status]');
    const defaultLoading = status?.textContent?.trim() || 'Loading reviews...';
    const errorMessage = status?.dataset.errorMessage || 'Unable to load reviews. Click to retry.';
    const config = extractStampedConfig(container) || { scriptSrc, apiKey, storeId };
    const widgetPlaceholder = target.querySelector('#stamped-main-widget');

    if (!container.dataset.stampedWidgetTemplate && widgetPlaceholder) {
      container.dataset.stampedWidgetTemplate = widgetPlaceholder.outerHTML;
      widgetPlaceholder.remove();
      container.dataset.stampedWidgetInjected = 'false';
    }

    const loadStamped = async () => {
      if (container.dataset.loaded === 'true' || container.dataset.loaded === 'loading') return;

      container.dataset.loaded = 'loading';
      setStatus(container, defaultLoading, 'loading');
      target.classList.add('is-loading');
      target.classList.remove('is-error');
      if (trigger) {
        trigger.setAttribute('aria-busy', 'true');
        trigger.disabled = true;
      }

      try {
        ensureStampedWidgetElement(container, target);
        await ensureStampedInit(config);
        if (window.StampedFn) {
          if (typeof window.StampedFn.loadWidget === 'function') {
            window.StampedFn.loadWidget();
          }
          if (typeof window.StampedFn.refresh === 'function') {
            window.StampedFn.refresh();
          } else if (typeof window.StampedFn.renderAll === 'function') {
            window.StampedFn.renderAll();
          }
        }

        container.dataset.loaded = 'true';
        target.classList.remove('is-loading');
        target.classList.add('is-loaded');
        if (placeholder) placeholder.remove();
        if (originalStyle) target.setAttribute('style', originalStyle);
        setStatus(container, '', 'loaded');
      } catch (error) {
        console.error('Lazy integrations: unable to load Stamped widget', error);
        container.dataset.loaded = 'error';
        target.classList.remove('is-loading');
        target.classList.add('is-error');
        if (originalStyle) target.setAttribute('style', originalStyle);
        setStatus(container, errorMessage, 'error');
        if (trigger) trigger.disabled = false;
      } finally {
        if (trigger) trigger.removeAttribute('aria-busy');
      }
    };

    container.addEventListener('click', () => {
      if (container.dataset.loaded === 'error' && !trigger) {
        loadStamped();
      }
    });

    attachActivationHandlers(trigger, loadStamped);

    if (auto) {
      let isVisible = false;
      const handleVisibility = () => {
        if (isVisible) return;
        isVisible = true;
        loadStamped();
      };
      observeVisibility(container, handleVisibility, `0px 0px ${threshold}px 0px`);
      
    } else if (!trigger) {
      loadStamped();
    }

    

    container.dataset.lazyReady = 'true';
  }

function initOrtery(container) {
  if (container.dataset.lazyReady === 'true') return;

  const trigger = container.querySelector('[data-integration-trigger]');
  const target = container.querySelector('[data-integration-target]');
  if (!target) return;
  const placeholder = target.querySelector('[data-integration-placeholder]');
  const iframeSrc = container.dataset.iframeSrc;
  if (!iframeSrc) return;
  const originalStyle = target.dataset.originalStyle || target.getAttribute('style') || '';
  target.dataset.originalStyle = originalStyle;

  const auto = container.dataset.lazyAuto === 'true';
  const threshold = container.dataset.lazyThreshold || '200';
  const status = container.querySelector('[data-integration-status]');
  const defaultLoading = status?.textContent?.trim() || 'Loading viewer...';
  const errorMessage = status?.dataset.errorMessage || 'Unable to load viewer. Click to retry.';

  const loadViewer = () => {
    if (container.dataset.loaded === 'true' || container.dataset.loaded === 'loading') return;

    container.dataset.loaded = 'loading';
    setStatus(container, defaultLoading, 'loading');
    target.classList.add('is-loading');
    target.classList.remove('is-error');
    if (trigger) {
      trigger.setAttribute('aria-busy', 'true');
      trigger.disabled = true;
    }

    try {
      const iframe = document.createElement('iframe');
      iframe.src = iframeSrc;
      iframe.loading = 'lazy';
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', container.dataset.iframeTitle || '');
      iframe.style.position = 'absolute';
      iframe.style.inset = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = '0';

      target.appendChild(iframe);
      if (placeholder) placeholder.remove();
      target.classList.add('is-loaded');
      target.classList.remove('is-loading');
      if (originalStyle) target.setAttribute('style', originalStyle);
      container.dataset.loaded = 'true';
      setStatus(container, '', 'loaded');
    } catch (error) {
      console.error('Lazy integrations: unable to load 360-degree viewer', error);
      container.dataset.loaded = 'error';
      target.classList.remove('is-loading');
      target.classList.add('is-error');
      if (originalStyle) target.setAttribute('style', originalStyle);
      setStatus(container, errorMessage, 'error');
      if (trigger) trigger.disabled = false;
    } finally {
      if (trigger) trigger.removeAttribute('aria-busy');
    }
  };

  container.addEventListener('click', () => {
    if (container.dataset.loaded === 'error' && !trigger) {
      loadViewer();
    }
  });

  attachActivationHandlers(trigger, loadViewer);

  if (auto) {
    observeVisibility(container, loadViewer, `0px 0px ${threshold}px 0px`);
  } else if (!trigger) {
    loadViewer();
  }

  container.dataset.lazyReady = 'true';
}

function initShopper(containers) {
    if (!containers.length || shopperListenersBound) return;

    const isMobile = window.matchMedia
      ? window.matchMedia(MOBILE_MEDIA_QUERY).matches
      : window.innerWidth <= 749;
    const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

    if (isMobile && !isDesignMode) {
      containers.forEach((container) => {
        container.dataset.loaded = 'skipped';
        container.setAttribute('hidden', 'true');
      });
      shopperListenersBound = true;
      return;
    }

    const urls = new Set();
    let intervalValue;
    let scheduled = false;

    containers.forEach((container) => {
      const attr = container.dataset.lazyScripts || '';
      attr.split(',').map((url) => url.trim()).filter(Boolean).forEach((url) => urls.add(url));

      if (!intervalValue && container.dataset.shopperInterval) {
        const parsed = parseInt(container.dataset.shopperInterval, 10);
        if (!Number.isNaN(parsed)) {
          intervalValue = parsed;
        }
      }
    });

    const loadShopper = () => {
      if (loadShopper.loaded) return;
      loadShopper.loaded = true;

      if (intervalValue) {
        window.sa_interval = intervalValue;
      }

      urls.forEach((url) => loadScriptOnce(url));
    };

    const scheduleLoad = () => {
      if (scheduled) return;
      scheduled = true;
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(loadShopper, { timeout: 1500 });
      } else {
        setTimeout(loadShopper, 750);
      }
    };

    containers.forEach((container) => {
      observeVisibility(container, scheduleLoad, '0px 0px 200px 0px');
    });

    setTimeout(scheduleLoad, 15000);
    shopperListenersBound = true;
  }

  function initLazyIntegrations(root = document) {
    const scope = root instanceof Element ? root : document;
    scope.querySelectorAll('[data-lazy-integration="stamped"]').forEach(initStamped);
    scheduleStampedPreload(scope);
    scope.querySelectorAll('[data-lazy-integration="ortery"]').forEach(initOrtery);
    const shopperContainers = scope.querySelectorAll('[data-lazy-integration="shopper"]');
    if (shopperContainers.length) {
      initShopper(shopperContainers);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initLazyIntegrations());
  } else {
    initLazyIntegrations();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event && event.target) {
      initLazyIntegrations(event.target);
    }
  });
})();
